import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { FileText, X } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  attachmentContentQueryOptions,
  attachmentPreviewQueryOptions,
} from "@/lib/data/react-query/chat-attachments";

const FileViewer = lazy(() =>
  import("@/components/features/matter/file-repo/FileViewer").then(
    (module) => ({ default: module.FileViewer }),
  ),
);

export interface ChatAttachmentPreviewFile {
  id: string;
  fileName: string;
  processingId?: string;
  pageCount?: number | null;
}

export function ChatAttachmentPreview({
  file,
  localFile,
  ready = true,
  onRemove,
}: {
  file: ChatAttachmentPreviewFile;
  localFile?: File;
  ready?: boolean;
  onRemove?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [visible, setVisible] = useState(false);
  const [localUrl, setLocalUrl] = useState<string>();
  const card = useRef<HTMLDivElement>(null);
  const image = /\.(png|jpe?g|webp)$/i.test(file.fileName);
  const localPreview = Boolean(localFile);
  useEffect(() => {
    if (!localPreview || !localFile) return;
    const url = URL.createObjectURL(localFile);
    setLocalUrl(url);
    return () => {
      URL.revokeObjectURL(url);
      setLocalUrl(undefined);
    };
  }, [localFile, localPreview]);
  useEffect(() => {
    const element = card.current;
    if (!element || localPreview || (!image && file.processingId)) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
      },
      { rootMargin: "100px" },
    );
    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [image, localPreview, file.processingId]);
  const content = useQuery({
    ...attachmentContentQueryOptions(file.id),
    enabled: !localPreview && !file.processingId && (open || visible),
  });
  const liveContent =
    !localPreview &&
    !file.processingId &&
    content.isSuccess &&
    content.isFetchedAfterMount
      ? content.data
      : undefined;
  const processingId =
    file.processingId ?? liveContent?.processingId ?? undefined;
  const pageCount = file.processingId ? file.pageCount : liveContent?.pageCount;
  const label =
    liveContent?.status === "failed"
      ? "Processing failed"
      : !ready || (liveContent && !processingId)
        ? "Processing…"
        : pageCount != null
          ? `${pageCount} ${pageCount === 1 ? "page" : "pages"}`
          : image
            ? "Image"
            : "Document";
  const preview = useQuery({
    ...attachmentPreviewQueryOptions(file.id, processingId),
    enabled: !localPreview && (open || (image && visible)),
  });
  const url = localUrl ?? preview.data?.url;
  const mimeType = localUrl ? localFile?.type : preview.data?.mimeType;
  return (
    <>
      <div
        ref={card}
        className="bg-muted/40 relative w-36 shrink-0 overflow-hidden rounded-xl border"
      >
        <button
          type="button"
          aria-label={`Preview ${file.fileName}`}
          className="hover:bg-muted flex w-full flex-col text-left"
          onClick={() => {
            setOpen(true);
            if (!localPreview) void preview.refetch();
          }}
        >
          <span className="bg-muted/40 flex h-20 w-full items-center justify-center overflow-hidden">
            {image && url ? (
              <img
                src={url}
                alt=""
                className="h-full w-full object-cover"
                loading="lazy"
                referrerPolicy="no-referrer"
              />
            ) : (
              <FileText className="text-muted-foreground size-7" />
            )}
          </span>
          <span
            className="w-full truncate px-2 pt-2 pb-1 text-xs font-medium"
            title={file.fileName}
          >
            {file.fileName}
          </span>
          <span className="text-muted-foreground px-2 pb-2 text-[11px]">
            {label}
          </span>
        </button>
        {onRemove && (
          <button
            type="button"
            aria-label={`Remove ${file.fileName}`}
            onClick={onRemove}
            className="bg-background/90 hover:bg-background absolute top-1 right-1 rounded-full border p-1"
          >
            <X className="size-3" />
          </button>
        )}
      </div>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex h-[85vh] flex-col sm:max-w-5xl">
          <DialogHeader>
            <DialogTitle className="break-words">{file.fileName}</DialogTitle>
            <DialogDescription>Attached file preview</DialogDescription>
          </DialogHeader>
          {!url && preview.isFetching && <p role="status">Loading preview…</p>}
          {!localUrl && preview.isError && (
            <div>
              <p role="alert">
                This file could not be opened. Please try again.
              </p>
              <Button
                type="button"
                variant="outline"
                onClick={() => void preview.refetch()}
              >
                Retry
              </Button>
            </div>
          )}
          {url &&
            (mimeType?.startsWith("image/") ? (
              <img
                src={url}
                alt={file.fileName}
                className="min-h-0 flex-1 object-contain"
                referrerPolicy="no-referrer"
              />
            ) : mimeType === "application/pdf" ? (
              <iframe
                title={`Preview ${file.fileName}`}
                src={url}
                className="min-h-0 flex-1 rounded-md border"
                referrerPolicy="no-referrer"
              />
            ) : (
              <Suspense fallback={<p role="status">Loading preview…</p>}>
                <div className="min-h-0 flex-1 overflow-auto">
                  <FileViewer
                    file={{ fileName: file.fileName, presignedURLKey: "" }}
                    fileType="firm-file"
                    presignedUrl={url}
                    showTitle={false}
                    showDownload={false}
                    canEditDocx={false}
                  />
                </div>
              </Suspense>
            ))}
        </DialogContent>
      </Dialog>
    </>
  );
}
